import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import zod from 'zod';
import { LprDeviceConfigEditionFormSchema } from '../../../../services/Devices/LprService/schemas';
import { Grid2 } from '@mui/material';
import TextFieldComponent from '../../../../components/TextFieldComponent';
import CheckboxComponent from '../../../../components/CheckboxComponent';
import TelegramSelectComponent from '../../../../components/TelegramSelectComponent';
import { useUpdateConfigLprDeviceAsync } from '../../../../services/Devices/LprService/hooks/useMutation';
import ButtonComponent from '../../../../components/ButtonComponent';
import { zodResolver } from '@hookform/resolvers/zod';
import { ILprDeviceProps } from '../../../../services/Devices/LprService/types';
import { AppContext } from '../../../../context/AppContextProvider';
import WhatsAppSelectComponent from '../../../../components/WhatsAppSelectComponent';
import MailSelectComponent from 'components/MailSelectComponent';
import { useSmtpIntegration } from '../../../../hooks/useSmtpIntegration';

interface LprDeviceConfigModalProps {
  isOpen: boolean;
  selectedDevice?: any;
  setSelectedDevice?: Dispatch<SetStateAction<any>>;
  onClose?: () => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

type LprDeviceConfigFormInput = zod.infer<
  typeof LprDeviceConfigEditionFormSchema
>;

const LprDeviceConfig = ({
  setIsOpen,
  setSelectedDevice,
  selectedDevice,
}: LprDeviceConfigModalProps) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<LprDeviceConfigFormInput>({
    resolver: zodResolver(LprDeviceConfigEditionFormSchema),
    defaultValues: {
      isOnlyPlate: false,
      maxSpeed: 0,
      telegramIds: [],
      whatsAppNumbers: [],
      emailAddresses: [],
      isNotifyLogEvent: false,
      isNotifyEvenWithoutGroups: false,
      smtpIntegrationId: null,
    },
  });
  const { showSnackbar } = useContext(AppContext);
  const [selectedDeviceToConfig, setSelectedDeviceToConfig] =
    useState<ILprDeviceProps>();
  const { smtpValue, onSmtpChange } = useSmtpIntegration(control, setValue);

  const { mutate: mutateUpdateConfig } = useUpdateConfigLprDeviceAsync();

  const handleClose = () => {
    setIsOpen(false);
    reset();
    if (setSelectedDevice) setSelectedDevice({});
  };

  const onSubmit = (data: LprDeviceConfigFormInput) => {
    if (selectedDeviceToConfig) {
      mutateUpdateConfig(
        { data, id: selectedDeviceToConfig.id },
        {
          onSuccess: () => {
            showSnackbar('Configuração atualizada com sucesso.', 'success');
            if (setSelectedDevice) setSelectedDevice({});
            reset();
            setIsOpen(false);
          },
        },
      );
    }
  };

  useEffect(() => {
    if (selectedDevice) {
      reset({
        ...selectedDevice,
        smtpIntegrationId: selectedDevice?.smtpIntegrationId || null,
      });
      setSelectedDeviceToConfig(selectedDevice);
    } else reset();
  }, [selectedDevice, reset]);

  return (
    <Grid2 container columnSpacing={3}>
      <Controller
        render={({ field: { value, onChange } }) => (
          <WhatsAppSelectComponent
            gridSize={12}
            onChange={onChange}
            value={value}
          />
        )}
        control={control}
        name={'whatsAppNumbers'}
      />
      <Controller
        render={({ field: { value, onChange } }) => (
          <TelegramSelectComponent
            gridSize={12}
            onChange={onChange}
            value={value}
          />
        )}
        control={control}
        name={'telegramIds'}
      />
      <Controller
        render={({ field: { value, onChange } }) => (
          <MailSelectComponent
            onChange={onChange}
            value={value}
            smtpValue={smtpValue}
            onSmtpChange={onSmtpChange}
          />
        )}
        control={control}
        name={'emailAddresses'}
      />
      <Grid2 size={{ md: 6, xs: 12 }}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <TextFieldComponent
              label="Velocidade máxima"
              onChangeText={(val) => onChange(Number(val))}
              type={'number'}
              value={value}
            />
          )}
          name={'maxSpeed'}
          control={control}
        />
      </Grid2>
      {/*<Grid2 size={6}>*/}
      {/*  <Controller*/}
      {/*    render={({ field: { value, onChange } }) => (*/}
      {/*      <CheckboxComponent*/}
      {/*        label="Somente BlackList"*/}
      {/*        onChange={onChange}*/}
      {/*        value={value}*/}
      {/*      />*/}
      {/*    )}*/}
      {/*    name={'isOnlyBlackList'}*/}
      {/*    control={control}*/}
      {/*  />*/}
      {/*</Grid2>*/}
      <Grid2 size={6}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <CheckboxComponent
              label="Notificar somente eventos que reconheçam placas"
              onChange={onChange}
              value={value}
            />
          )}
          name={'isOnlyPlate'}
          control={control}
        />
      </Grid2>
      <Grid2 size={6}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <CheckboxComponent
              label="Notificar TODAS as placas mesmo sem estar em um grupo de placas"
              onChange={onChange}
              value={value}
            />
          )}
          name={'isNotifyEvenWithoutGroups'}
          control={control}
        />
      </Grid2>
      <Grid2 size={6}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <CheckboxComponent
              label="Notificar eventos de Log"
              onChange={onChange}
              value={value}
            />
          )}
          name={'isNotifyLogEvent'}
          control={control}
        />
      </Grid2>
      <Grid2 size={12} sx={{ mt: 3 }}>
        <ButtonComponent fullWidth onClick={handleSubmit(onSubmit)}>
          Salvar
        </ButtonComponent>
      </Grid2>
    </Grid2>
  );
};

export default LprDeviceConfig;
